import { type CellFormat, columnFormat, displayCell } from '@shared/cell-format';
import type { DataTableCell } from '@shared/types/views';
import type { AppCollection, AppRecord } from '@/lib/app-records';
import { cellText } from './app-records-model';
import { CellValue } from './CellValue';

/** A text that reads in one glance sits on a line with the others; a longer one gets its own paragraph. */
const SHORT_VALUE = 40;
/** Formats that always read in one glance (U2). */
const GLANCE: ReadonlySet<CellFormat> = new Set(['number', 'money', 'percent', 'date', 'rating', 'decision', 'link', 'email', 'tags']);

interface AppRecordFieldsProps {
  record: AppRecord;
  collection: AppCollection;
  /** The columns the list shows: they decide, so they come first. */
  deciding: string[];
  /** U2: the formats read over the whole list, when the screen has it. */
  formats?: Record<string, CellFormat>;
}

/** What Domino found on an object, as a pyramid: what decides first, then the facts that support it. */
export const AppRecordFields = ({ record, collection, deciding, formats }: AppRecordFieldsProps) => {
  const labelOf = (key: string) => collection.columns.find((column) => column.key === key)?.label ?? key;
  const fields = record.computed.slice(1).filter((cell) => cellText(cell));
  const top = deciding.flatMap((key) => fields.filter((cell) => cell.key === key));
  const rest = fields.filter((cell) => !deciding.includes(cell.key));
  const formatOf = (cell: DataTableCell): CellFormat => {
    const column = collection.columns.find((item) => item.key === cell.key);
    return formats?.[cell.key] ?? (column ? columnFormat(column, [cell]) : 'text');
  };
  const value = (cell: DataTableCell) => <CellValue display={displayCell(formatOf(cell), cell)} tone={cell.tone} />;
  const short = top.filter((cell) => formatOf(cell) !== 'long_text' && (GLANCE.has(formatOf(cell)) || cellText(cell).length <= SHORT_VALUE));
  const long = top.filter((cell) => !short.includes(cell));

  const label = (cell: DataTableCell) => (
    <>
      {labelOf(cell.key)}
      {record.edits[cell.key] && (
        <span className="ml-1.5 font-normal" title={`Valeur de Domino : ${cellText(record.computedByDomino.find((item) => item.key === cell.key)) || '(vide)'}`}>
          · corrigé{record.edits[cell.key]!.source === 'chat' ? ' depuis le chat' : ''}
        </span>
      )}
    </>
  );

  if (fields.length === 0) return null;
  return (
    <div className="space-y-4">
      {top.length > 0 && (
        <div className="space-y-3 rounded-xl bg-domino-warm p-4">
          {short.length > 0 && (
            <dl className="flex flex-wrap gap-x-8 gap-y-3">
              {short.map((cell) => (
                <div key={cell.key}>
                  <dt className="text-xs text-domino-inkSoft">{label(cell)}</dt>
                  <dd className="mt-0.5 text-base font-semibold text-domino-ink">{value(cell)}</dd>
                </div>
              ))}
            </dl>
          )}
          {long.map((cell) => (
            <dl key={cell.key}>
              <dt className="text-xs text-domino-inkSoft">{label(cell)}</dt>
              <dd className="mt-0.5 whitespace-pre-wrap text-sm leading-6 text-domino-ink">{value(cell)}</dd>
            </dl>
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <dl className="divide-y divide-domino-cardBorder">
          {rest.map((cell) => (
            <div key={cell.key} className="grid gap-1 py-2 sm:grid-cols-[minmax(8rem,30%)_1fr] sm:gap-4">
              <dt className="text-xs text-domino-inkSoft sm:pt-0.5">{label(cell)}</dt>
              <dd className="whitespace-pre-wrap text-sm leading-6 text-domino-ink">{value(cell)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};
